import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const Footer = ({ onBookInspection }) => {
  const { t, selectedCountry } = useCurrency();

  const footerLinks = {
    products: [
      { label: t('footer.inspection'), href: '#inspection' },
      { label: t('footer.warranty'), href: '#warranty' },
      { label: t('footer.financing'), href: '#financing' },
      { label: t('footer.forBusiness'), href: '#plans' },
    ],
    company: [
      { label: t('footer.about'), href: '#about' },
      { label: t('nav.howItWorks'), href: '#how-it-works' },
      { label: t('nav.technology'), href: '#technology' },
      { label: t('footer.careers'), href: '#careers' },
    ],
    support: [
      { label: t('footer.helpCenter'), href: '#help' },
      { label: t('footer.contact'), href: '#contact' },
      { label: t('faq.badge'), href: '#faq' },
      { label: t('claimModal.title'), href: '#claims' },
    ],
    legal: [
      { label: t('footer.privacy'), href: `/${selectedCountry.code.toLowerCase()}/privacy` },
      { label: t('footer.terms'), href: `/${selectedCountry.code.toLowerCase()}/terms` },
      { label: t('footer.refund'), href: `/${selectedCountry.code.toLowerCase()}/refund` },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  const isIndia = selectedCountry.code === 'IN';

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img
              src="/wisedrive-logo-new.png"
              alt="Wisedrive"
              className="h-8 mb-4"
            />
            <p className="text-slate-400 mb-6 leading-relaxed">
              {t('footer.tagline')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-teal-400" />
                <span>support@wisedrive.com</span>
              </div>
              <a
                href={isIndia ? 'tel:+917338073380' : 'tel:+60122559610'}
                data-testid="footer-call-us"
                className="flex items-center gap-3 text-sm hover:text-teal-400 transition-colors"
              >
                <Phone className="h-4 w-4 text-teal-400" />
                <span>{isIndia ? '+91 73380 73380' : '+60 122559610'}</span>
              </a>
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="h-4 w-4 text-teal-400" />
                <span>{isIndia ? 'India & 15+ Countries' : 'Malaysia & Southeast Asia'}</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm hover:text-teal-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm hover:text-teal-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.support')}</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm hover:text-teal-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-teal-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="h-10 w-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} Wisedrive. {t('footer.copyright')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
